import { useState, useCallback, useRef } from "react";
import { buildAvatarUrl, DEFAULT_OPTIONS, REGIONS } from "./AvatarBuilder";
import { saveAvatarBuilderConfig, SECTION_META, COLOR_GROUPS } from "./avatarBuilderConfig";

const PREVIEW_BASE = { ...DEFAULT_OPTIONS, seed: "admin-preview", backgroundColor: "ffffff" };

const SECTION_PARAM = { hair: "top", eyes: "eyes", eyebrows: "eyebrows", mouth: "mouth", clothing: "clothing" };

const ADMIN_SCALE = 0.6;

function CropPreview({ sectionKey, opt }) {
  const r = REGIONS[sectionKey];
  const svgW = r.x2 - r.x1;
  const svgH = r.y2 - r.y1;
  const scale = (r.w * ADMIN_SCALE) / svgW;
  const W = Math.round(r.w * ADMIN_SCALE);
  const H = Math.round(svgH * scale);
  const imgSize = Math.round(280 * scale);
  const left = Math.round(-r.x1 * scale);
  const top  = Math.round(-r.y1 * scale);

  let overrides = {};
  if (sectionKey === "accessories") {
    overrides = { accessories: opt.accessories, accessoriesProbability: opt.accessoriesProbability };
  } else if (sectionKey === "facialHair") {
    overrides = { facialHair: opt.facialHair, facialHairProbability: opt.facialHairProbability };
  } else {
    overrides = { [SECTION_PARAM[sectionKey]]: opt.id };
  }

  const url = buildAvatarUrl({ ...PREVIEW_BASE, ...overrides });

  return (
    <div style={{ flexShrink: 0, width: W, height: H, borderRadius: "6px", overflow: "hidden", position: "relative" }}>
      <img src={url} width={imgSize} height={imgSize} loading="lazy"
        style={{ position: "absolute", left, top, display: "block", pointerEvents: "none" }} />
    </div>
  );
}

export default function AvatarBuilderAdmin({ config, onConfigSaved }) {
  const [local, setLocal] = useState(() => JSON.parse(JSON.stringify(config)));
  const [activeSection, setActiveSection] = useState("hair");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dragSrcIdx, setDragSrcIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragNodeRef = useRef(null);

  const section = local.sections[activeSection];
  const sortedOptions = [...(section?.options ?? [])].sort((a, b) => a.order - b.order);

  const updateOption = useCallback((idx, changes) => {
    setLocal((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const opts = [...next.sections[activeSection].options].sort((a, b) => a.order - b.order);
      Object.assign(opts[idx], changes);
      next.sections[activeSection].options = opts;
      return next;
    });
  }, [activeSection]);

  // Moves fromIdx to toIdx, re-assigning sequential order values.
  const dropOption = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setLocal((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const opts = [...next.sections[activeSection].options].sort((a, b) => a.order - b.order);
      const [item] = opts.splice(fromIdx, 1);
      opts.splice(toIdx, 0, item);
      opts.forEach((o, i) => { o.order = i; });
      next.sections[activeSection].options = opts;
      return next;
    });
  }, [activeSection]);

  const setDefaultOption = useCallback((optId) => {
    setLocal((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[activeSection] = { ...next.sections[activeSection], defaultId: optId };
      return next;
    });
  }, [activeSection]);

  const toggleAllInSection = (enabled) => {
    setLocal((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[activeSection].options = next.sections[activeSection].options.map((o) => ({ ...o, enabled }));
      return next;
    });
  };

  const setSectionDisplayMode = (mode) => {
    setLocal((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[activeSection] = { ...next.sections[activeSection], displayMode: mode };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAvatarBuilderConfig(local);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onConfigSaved(local);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const sectionKeys = Object.keys(SECTION_META);
  const allEnabled = sortedOptions.every((o) => o.enabled);
  const allDisabled = sortedOptions.every((o) => !o.enabled);
  const sectionDisplayMode = section?.displayMode || "icons";

  return (
    <div style={{ background: "#f5faff", borderRadius: "20px", padding: "20px", border: "1.5px solid #dbeafe" }}>
      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "16px" }}>Avatar Builder Settings</div>

      {/* Section tabs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "14px" }}>
        {sectionKeys.map((key) => (
          <button key={key} type="button" onClick={() => setActiveSection(key)}
            style={{
              padding: "6px 12px", borderRadius: "10px", fontSize: "12px", fontWeight: "bold", cursor: "pointer",
              border: activeSection === key ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: activeSection === key ? "#dbeafe" : "#fff",
              color: activeSection === key ? "#1d4ed8" : "#4b6b8a",
            }}>
            {SECTION_META[key].label}
          </button>
        ))}
      </div>

      {/* Per-section controls: display mode + show all/none */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {SECTION_META[activeSection].label} style:
        </span>
        {[["icons", "🖼 Images"], ["names", "Aa Text"]].map(([mode, label]) => (
          <button key={mode} type="button"
            onClick={() => setSectionDisplayMode(mode)}
            style={{
              padding: "4px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "bold", cursor: "pointer",
              border: sectionDisplayMode === mode ? "2px solid #2563eb" : "1.5px solid #bfdbfe",
              background: sectionDisplayMode === mode ? "#dbeafe" : "#fff",
              color: sectionDisplayMode === mode ? "#1d4ed8" : "#4b6b8a",
            }}>
            {label}
          </button>
        ))}

        <div style={{ width: "1px", height: "18px", background: "#e2e8f0", margin: "0 4px" }} />

        <span style={{ fontSize: "12px", color: "#4b6b8a", fontWeight: "600" }}>Show:</span>
        <button type="button" onClick={() => toggleAllInSection(true)} disabled={allEnabled}
          style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: allEnabled ? "#e0f2fe" : "#fff", color: "#1e3a5f", cursor: "pointer" }}>
          All
        </button>
        <button type="button" onClick={() => toggleAllInSection(false)} disabled={allDisabled}
          style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: allDisabled ? "#e0f2fe" : "#fff", color: "#1e3a5f", cursor: "pointer" }}>
          None
        </button>
        <span style={{ fontSize: "11px", color: "#94a3b8" }}>
          {sortedOptions.filter((o) => o.enabled).length} / {sortedOptions.length} visible
        </span>
      </div>

      {/* Options list — drag to reorder */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>
        {sortedOptions.map((opt, idx) => {
          const isDragging = dragSrcIdx === idx;
          const isTarget  = dragOverIdx === idx && dragOverIdx !== dragSrcIdx;
          const isDefault = (section?.defaultId ?? null) === opt.id;
          return (
            <div
              key={opt.id}
              draggable
              ref={isDragging ? dragNodeRef : null}
              onDragStart={(e) => {
                if (e.target instanceof HTMLInputElement) { e.preventDefault(); return; }
                e.dataTransfer.effectAllowed = "move";
                setDragSrcIdx(idx);
              }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverIdx !== idx) setDragOverIdx(idx); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverIdx(null); }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragSrcIdx !== null) dropOption(dragSrcIdx, idx);
                setDragSrcIdx(null);
                setDragOverIdx(null);
              }}
              onDragEnd={() => { setDragSrcIdx(null); setDragOverIdx(null); }}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 10px", borderRadius: "12px",
                background: opt.enabled ? "#fff" : "#f1f5f9",
                border: isTarget ? "2px solid #2563eb" : "1.5px solid #e2e8f0",
                opacity: isDragging ? 0.35 : (opt.enabled ? 1 : 0.55),
                cursor: "grab",
                transition: "border 0.1s, opacity 0.1s",
                boxShadow: isTarget ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
              }}
            >
              {/* Drag handle */}
              <span style={{ color: "#c0ccd8", fontSize: "16px", userSelect: "none", flexShrink: 0, letterSpacing: "-2px", lineHeight: 1 }}>⠿</span>

              {/* Crop preview */}
              <CropPreview sectionKey={activeSection} opt={opt} />

              {/* Enable toggle */}
              <input
                type="checkbox"
                checked={opt.enabled}
                onChange={(e) => updateOption(idx, { enabled: e.target.checked })}
                draggable={false}
                style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
              />

              {/* Default star */}
              <button
                type="button"
                draggable={false}
                title={isDefault ? "Default for new users (click to unset)" : "Set as default for new users"}
                onClick={() => setDefaultOption(isDefault ? null : opt.id)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "18px", padding: "0 2px", lineHeight: 1, flexShrink: 0,
                  color: isDefault ? "#f59e0b" : "#cbd5e1",
                }}
              >
                {isDefault ? "★" : "☆"}
              </button>

              {/* Label editor */}
              <input
                type="text"
                value={opt.label}
                onChange={(e) => updateOption(idx, { label: e.target.value })}
                draggable={false}
                style={{
                  flex: 1, fontSize: "13px", padding: "5px 8px",
                  borderRadius: "8px", border: "1.5px solid #e2e8f0",
                  background: "#f8fbff", color: "#1e3a5f", minWidth: 0,
                }}
                placeholder="Label…"
              />

              {/* Value tag */}
              <span style={{ fontSize: "10px", color: "#94a3b8", fontFamily: "monospace", flexShrink: 0 }}>{opt.id}</span>
            </div>
          );
        })}
      </div>

      {/* Color palette section */}
      <div style={{ marginTop: "20px", borderTop: "1.5px solid #dbeafe", paddingTop: "16px" }}>
        <div style={{ fontWeight: "700", color: "#1e3a5f", fontSize: "13px", marginBottom: "12px" }}>Color Palettes</div>
        {Object.entries(COLOR_GROUPS).map(([groupKey, groupDef]) => {
          const savedOpts = local.colors?.[groupKey]?.options ?? groupDef.options;
          return (
            <div key={groupKey} style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>
                {groupDef.label}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {savedOpts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    title={`${c.label} — click to ${c.enabled ? "hide" : "show"}`}
                    onClick={() => {
                      setLocal((prev) => {
                        const next = JSON.parse(JSON.stringify(prev));
                        const group = next.colors?.[groupKey] ?? { ...groupDef };
                        group.options = (group.options ?? groupDef.options).map((o) =>
                          o.id === c.id ? { ...o, enabled: !o.enabled } : o
                        );
                        next.colors = { ...(next.colors ?? {}), [groupKey]: group };
                        return next;
                      });
                    }}
                    style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: `#${c.id}`,
                      border: c.enabled ? "2.5px solid #2563eb" : "2px solid #e2e8f0",
                      cursor: "pointer",
                      opacity: c.enabled ? 1 : 0.3,
                      position: "relative",
                      outline: "none",
                    }}
                  >
                    {!c.enabled && (
                      <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", color: "#64748b", pointerEvents: "none" }}>×</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Save */}
      <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button type="button" onClick={handleSave} disabled={saving}
          style={{ padding: "10px 24px", borderRadius: "14px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {saved && <span style={{ color: "#16a34a", fontSize: "13px", fontWeight: "600" }}>✓ Saved</span>}
      </div>
    </div>
  );
}
